import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Nav, Navbar, Button, Dropdown, NavbarCollapse, NavbarToggle
} from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../Images/AZ.png";
import { toast, ToastContainer } from 'react-toastify';
import axios from "axios";
import { FiUser, FiX, FiLogOut, FiMenu, FiChevronLeft, FiHome, FiInfo, FiPackage, FiCreditCard, FiBook } from "react-icons/fi";

const NAV_LINKS = [
  { to: "/", label: "Home", icon: FiHome },
  { to: "/our-story", label: "About", icon: FiInfo },
  { to: "/products", label: "Products", icon: FiPackage },
  { to: "/dbs-emi", label: "EMI", icon: FiCreditCard },
  { to: "/blog", label: "Blogs", icon: FiBook },
];

const Header = () => {
  const [userData, setUserData] = useState(null);
  const [profileColor, setProfileColor] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Load user data
  useEffect(() => {
    const stored = localStorage.getItem("userData");
    if (stored) {
      const user = JSON.parse(stored);
      setUserData(user);

      const userColorKey = `profileColor-${user.email_address}`;
      let color = localStorage.getItem(userColorKey);

      if (!color) {
        color = `hsl(${Math.floor(Math.random() * 360)}, 70%, 60%)`;
        localStorage.setItem(userColorKey, color);
      }
      setProfileColor(color);
    }
  }, []);

  const getProfileInitial = useCallback((email) =>
    email?.charAt(0).toUpperCase() || "U", []
  );

  const isActiveLink = useCallback((path) => location.pathname === path, [location.pathname]);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post('https://www.azzenterprises.com/api/customers/signout', {}, { withCredentials: true });
      toast.success('Logged out successfully');

      localStorage.removeItem("userData");
      if (userData?.email_address) {
        localStorage.removeItem(`profileColor-${userData.email_address}`);
      }

      setTimeout(() => {
        navigate("/sign-in");
        setIsExpanded(false);
      }, 1500);
    } catch (err) {
      toast.error('Logout failed');
    }
  }, [userData, navigate]);

  const ProfileAvatar = ({ size = 40 }) => (
    <div
      className="d-flex align-items-center justify-content-center rounded-circle border shadow-sm"
      style={{
        width: size,
        height: size,
        backgroundColor: profileColor || '#e9ecef',
        color: profileColor ? 'white' : '#6c757d',
        fontSize: `${size / 2.2}px`,
        fontWeight: 600,
      }}
    >
      {getProfileInitial(userData?.email_address)}
    </div>
  );

  const MobileNavLink = ({ to, label, icon: Icon, onClick }) => (
    <Nav.Link
      as={Link}
      to={to}
      className={`py-3 px-4 border-start border-3 rounded-0 d-flex align-items-center transition-all ${isActiveLink(to)
        ? 'text-primary bg-primary bg-opacity-10 border-primary shadow-sm'
        : 'text-muted hover:text-primary hover:bg-light border-transparent'
        }`}
      onClick={onClick}
      style={{ fontSize: '1rem', fontWeight: 500 }}
    >
      <Icon size={20} className={`me-3 flex-shrink-0 ${isActiveLink(to) ? 'text-primary' : 'text-muted'}`} />
      {label}
    </Nav.Link>
  );

  return (
    <>
      {/* Main Navbar */}
      <Navbar
        expand="md"
        className="navbar-light sticky-top shadow-sm"
        style={{
          background: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'saturate(180%) blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          zIndex: 1050
        }}
        expanded={isExpanded}
      >
        <Container fluid="xl" className="px-3 px-md-4">
          {/* Logo - Responsive */}
          <Navbar.Brand as={Link} to="/" className="p-0 me-3 me-md-4">
            <img
              src={logo}
              alt="AZ"
              width={44}
              height={44}
              className="rounded-3 shadow-sm d-inline-block"
              style={{ minWidth: '44px' }}
            />
          </Navbar.Brand>

          {/* Desktop Nav - lg and up */}
          <NavbarCollapse className="d-none d-lg-flex justify-content-end flex-grow-1">
            <Nav className="align-items-center gap-1 gap-lg-2">
              {NAV_LINKS.map(({ to, label }) => (
                <Nav.Link
                  key={to}
                  as={Link}
                  to={to}
                  className={`px-3 px-lg-4 py-2 fw-medium rounded-3 transition-all lh-1 ${isActiveLink(to)
                    ? 'text-primary bg-primary bg-opacity-10 shadow-sm border border-primary border-opacity-25'
                    : 'text-muted hover:text-primary hover:bg-primary hover:bg-opacity-5'
                    }`}
                  style={{ fontSize: '0.9rem' }}
                >
                  {label}
                </Nav.Link>
              ))}

              {/* Auth Controls */}
              <div className="ms-2 ms-lg-3 d-flex align-items-center">
                {!userData ? (
                  <Button
                    as={Link}
                    to="/sign-in"
                    variant="primary"
                    className="rounded-pill px-4 py-1.5 fw-semibold shadow-sm border-0"
                    size="sm"
                  >
                    Sign in
                  </Button>
                ) : (
                  <Dropdown align={{ lg: 'end' }} className="ms-1 ms-lg-2">
                    <Dropdown.Toggle
                      variant="link"
                      bsPrefix="p-0 border-0 shadow-none rounded-circle"
                      className="text-decoration-none hover:shadow-sm transition-all"
                    >
                      <ProfileAvatar size={36} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      className="shadow-xl border-0 mt-2 p-0 rounded-4 overflow-hidden"
                      style={{ minWidth: '240px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)' }}
                    >
                      <Dropdown.Header className="py-3 px-4 border-bottom bg-light">
                        <div className="d-flex align-items-center">
                          <ProfileAvatar size={36} />
                          <div className="ms-3">
                            <div className="fw-semibold fs-6 lh-1">{userData.name}</div>
                            <small className="text-muted d-block">{userData.email_address}</small>
                          </div>
                        </div>
                      </Dropdown.Header>

                      <Dropdown.Item
                        as={Link}
                        to="/personal"
                        className={`px-4 py-3 border-bottom border-top-0 ${location.pathname.startsWith('/personal') && 'bg-primary bg-opacity-10 text-primary fw-semibold'
                          }`}
                      >
                        <FiUser className="me-3 mb-1" size={20} />
                        <span>My Profile</span>
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={handleLogout}
                        className="px-4 py-3 text-danger hover:bg-danger hover:bg-opacity-10 fw-medium"
                      >
                        <FiLogOut className="me-3 mb-1" size={20} />
                        <span>Sign Out</span>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </Nav>
          </NavbarCollapse>

          {/* Mobile Menu Toggle - md and down */}
          <NavbarToggle
            onClick={() => setIsExpanded(!isExpanded)}
            className="ms-auto p-1 rounded-2 shadow-sm border-0 d-lg-none me-1"
            aria-label="Toggle navigation"
          >
            <FiMenu size={20} className={`transition-all ${isExpanded ? 'text-primary' : 'text-muted'}`} />
          </NavbarToggle>
        </Container>
      </Navbar>

      {/* Mobile/Tablet Fullscreen Menu */}
      <div
        className={`position-fixed top-0 start-0 w-100 h-100 d-lg-none bg-dark bg-opacity-${isExpanded ? '50' : '0'} z-3 transition-all ${isExpanded ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        style={{ transition: 'opacity 0.3s ease' }}
        onClick={() => setIsExpanded(false)}
      >
        <div
          className="position-absolute top-0 start-0 h-100 bg-white shadow-2xl rounded-end-4"
          style={{
            width: 'min(85vw, 380px)',
            transform: isExpanded ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile Header */}
          <div className="p-4 border-bottom d-flex align-items-center">
            <Button
              variant="link"
              className="p-0 me-3 text-muted shadow-none rounded-2 hover:shadow-sm"
              onClick={() => setIsExpanded(false)}
            >
              <FiChevronLeft size={24} />
            </Button>
            <Link to="/" className="d-flex align-items-center text-decoration-none flex-grow-1">
              <img src={logo} alt="AZ" width={36} height={36} className="rounded-2 me-2 shadow-sm" />
              <span className="h6 mb-0 fw-bold text-dark lh-1">AZ</span>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="px-0">
            <Nav className="flex-column">
              {NAV_LINKS.map(({ to, label, icon: Icon }) => (
                <MobileNavLink
                  key={to}
                  to={to}
                  label={label}
                  icon={Icon}
                  onClick={() => setIsExpanded(false)}
                />
              ))}
            </Nav>
          </div>

          {/* Auth Section */}
          <div className="border-top mt-auto p-5">
            {!userData ? (
              <Button
                as={Link}
                to="/sign-in"
                className="w-100 py-3 px-4 rounded-3 fw-semibold shadow-lg border-0"
                variant="primary"
                size="lg"
                onClick={() => setIsExpanded(false)}
              >
                Get Started
              </Button>
            ) : (
              <div className="text-center pb-3">
                <ProfileAvatar size={72} />
                <div className="mt-4 mb-5">
                  <h6 className="mb-1 fw-bold text-dark">{userData.name}</h6>
                  <small className="text-muted">{userData.email_address}</small>
                </div>
                <div className="d-grid gap-2">
                  <Button
                    as={Link}
                    to="/personal"
                    variant="outline-primary"
                    className="rounded-3 py-3 px-4 fw-semibold shadow-sm"
                    size="lg"
                    onClick={() => setIsExpanded(false)}
                  >
                    <FiUser className="me-2 mb-1 d-inline" size={20} />
                    My Profile
                  </Button>
                  <Button
                    variant="outline-danger"
                    className="rounded-3 py-3 px-4 fw-semibold shadow-sm"
                    size="lg"
                    onClick={() => {
                      handleLogout();
                      setIsExpanded(false);
                    }}
                  >
                    <FiLogOut className="me-2 mb-1 d-inline" size={20} />
                    Sign Out
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        theme="colored"
        className="mt-20"
        toastClassName="rounded-3 shadow-lg"
      />
    </>
  );
};

export default Header;
